"use client";

/**
 * Loads the TRX PayPage Fields JS library
 * (`paypage-fields.trxservices.{net|com}/bundle.js`) once per browser tab
 * and surfaces readiness state to React components.
 *
 * This is THE loader — the only implementation. The bundle host is DERIVED
 * FROM THE APP ENVIRONMENT (`resolveTrxPayPageFieldsUrl`), exactly like the
 * API gateway and every service URL — prod → `.com`, else → `.net` (UAT).
 * NO env var is required in any environment; the thin app wrappers
 * (`apps/checkout/.../useTrxPayPageScript.ts` on Vite, `apps/management/.../
 * useTrxPayPageScript.ts` on Next) may still pass a URL as an OPTIONAL
 * override (local tunnel/mock) and widen the returned global to the
 * card/wallet-capable surface their frames consume. An unset override no
 * longer silently means UAT — that mismatch is what broke prod checkout on
 * 2026-07-28.
 *
 * Why a hook: the library is delivered as a classical `<script>` (not an
 * ES module) — there's no `import 'paypage-fields'` path. We inject the
 * script tag, wait for `window.PayPageJS` to materialize, and cache the
 * result so multiple consumers on the same page share one script load.
 */

import { useCallback, useEffect, useState } from "react";
import { getAppEnv, type AppEnv } from "../../utils/environment";
import {
  getPayPageJsGlobal,
  type TrxPayPageGlobal,
} from "./trxPayPageTypes";

/** UAT / cert PayPage Fields bundle — served off the `.net` gateway family. */
export const TRX_PAYPAGE_FIELDS_UAT_URL =
  "https://paypage-fields.trxservices.net/bundle.js";

/** Production PayPage Fields bundle — served off the `.com` gateway family. */
export const TRX_PAYPAGE_FIELDS_PROD_URL =
  "https://paypage-fields.trxservices.com/bundle.js";

/**
 * Resolve the correct PayPage Fields bundle host FROM THE ENVIRONMENT — the
 * same way the API gateway (`resolveTrxApiUrl`) and every service URL already
 * key off the resolved app env. The browser fields library MUST be the same
 * TRX environment as the session-minting gateway (they are fully isolated: a
 * prod-minted session handed to the UAT fields lib fails to initialize), so
 * this cannot be left to a per-environment env var that someone has to remember
 * to set — an unset var silently defaults to UAT, which is exactly what broke
 * production checkout on 2026-07-28.
 *
 *   production            → `.com`
 *   staging / development → `.net`  (UAT)
 *
 * `explicitOverride` is an escape hatch ONLY (e.g. a local tunnel/mock); when
 * absent — the normal case — the host is derived and NO env var is required in
 * any environment. `appEnv` defaults to the shared {@link getAppEnv} reader.
 */
export function resolveTrxPayPageFieldsUrl(
  explicitOverride?: string,
  appEnv: AppEnv = getAppEnv(),
): string {
  if (explicitOverride) return explicitOverride;
  return appEnv === "production"
    ? TRX_PAYPAGE_FIELDS_PROD_URL
    : TRX_PAYPAGE_FIELDS_UAT_URL;
}

/**
 * Module-level cache. Multiple components mounting on the same page share
 * the same load promise — we never inject a second script tag. Keyed by
 * URL so a UAT → production env change loads the new bundle.
 */
const loadCache = new Map<string, Promise<TrxPayPageGlobal>>();

/**
 * Ceiling on the bundle load. A script tag that never fires `load` OR `error`
 * (a stalled CDN connection on a phone) used to leave this promise pending
 * forever — and because it is cached, every later mount in the tab waited on
 * the same dead promise, so the card form sat on its shimmer until the
 * customer reloaded. Past this the load REJECTS, the cache entry and the tag
 * are dropped, and the next mount injects a fresh tag.
 */
export const TRX_PAYPAGE_SCRIPT_TIMEOUT_MS = 10_000;

/**
 * Why TRX's PayPage-Fields bundle did not become usable:
 *   - `timeout`   — no load/error event within {@link TRX_PAYPAGE_SCRIPT_TIMEOUT_MS}
 *                   (slow connection, or a filter that silently drops the request);
 *   - `network`   — the browser refused or failed the download (offline, DNS,
 *                   firewall, VPN, content blocker);
 *   - `no-global` — it downloaded but never defined `window.PayPageJS`.
 */
export type TrxPayPageScriptFailureReason = "timeout" | "network" | "no-global";

export class TrxPayPageScriptError extends Error {
  constructor(
    readonly reason: TrxPayPageScriptFailureReason,
    readonly url: string,
    message: string,
  ) {
    super(message);
    this.name = "TrxPayPageScriptError";
  }
}

function scriptHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/**
 * The sentence an operator or payer sees when the bundle never loaded. It names
 * the cause class and the host, because the fix is on THEIR side of the wire
 * (connection, blocker, network policy) far more often than on ours.
 */
export function describeTrxScriptFailure(error: Error): string {
  if (error instanceof TrxPayPageScriptError) {
    const host = scriptHost(error.url);
    switch (error.reason) {
      case "timeout":
        return `The secure payment form took too long to load from ${host}. Check the connection and try again.`;
      case "network":
        return `The secure payment form could not be downloaded from ${host}. The connection, a firewall, VPN or content blocker may be blocking it. Try again, or try another network.`;
      case "no-global":
        return "The secure payment form downloaded but did not start. Try again; if it keeps happening, contact OrderUp support.";
    }
  }
  return "The secure payment form failed to load. Try again.";
}

function loadScript(url: string): Promise<TrxPayPageGlobal> {
  const cached = loadCache.get(url);
  if (cached !== undefined) return cached;

  const promise = new Promise<TrxPayPageGlobal>((resolve, reject) => {
    // If the global is already on window (HMR / cached load), short-
    // circuit. The library is non-idempotent on second script load —
    // re-injecting can leave orphaned listeners.
    const existing_global = getPayPageJsGlobal();
    if (existing_global) {
      resolve(existing_global);
      return;
    }

    // Re-use any existing tag from a prior init attempt rather than
    // injecting a duplicate. Defensive against race conditions when the
    // cache was cleared but the tag was not.
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${url}"]`,
    );
    const script = existing ?? document.createElement("script");
    if (!existing) {
      script.src = url;
      script.async = true;
      script.defer = false;
      script.crossOrigin = "anonymous";
    }

    const onLoad = () => {
      cleanup();
      const loaded = getPayPageJsGlobal();
      if (loaded) {
        resolve(loaded);
      } else {
        // Uncache, or every later mount is handed this same rejection.
        loadCache.delete(url);
        reject(
          new TrxPayPageScriptError(
            "no-global",
            url,
            `TRX PayPage Fields script loaded from ${url} but window.PayPageJS is undefined`,
          ),
        );
      }
    };
    const onError = () => {
      cleanup();
      // Drop the cached promise on failure so the next mount can retry.
      // Without this a single transient network hiccup would brick the
      // form for the whole session.
      loadCache.delete(url);
      reject(
        new TrxPayPageScriptError(
          "network",
          url,
          `Failed to load TRX PayPage Fields script: ${url}`,
        ),
      );
    };
    const onTimeout = () => {
      cleanup();
      loadCache.delete(url);
      // Remove the stalled tag, or the retry's querySelector above would
      // find it and wait on the same dead connection again.
      script.remove();
      reject(
        new TrxPayPageScriptError(
          "timeout",
          url,
          `Timed out after ${TRX_PAYPAGE_SCRIPT_TIMEOUT_MS}ms loading TRX PayPage Fields script: ${url}`,
        ),
      );
    };
    const timer = setTimeout(onTimeout, TRX_PAYPAGE_SCRIPT_TIMEOUT_MS);
    const cleanup = () => {
      clearTimeout(timer);
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);
    };

    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);
    if (!existing) document.head.appendChild(script);
  });

  loadCache.set(url, promise);
  return promise;
}

export interface UseTrxPayPageScriptResult {
  readonly ready: boolean;
  readonly error: Error | null;
  readonly PayPageJS: TrxPayPageGlobal | null;
  /**
   * Load the bundle again after a failure. A failed load is never cached
   * (`loadScript` drops it), so this is a real second download, not a replay.
   */
  readonly retry: () => void;
}

export function useTrxPayPageScript(
  scriptUrl?: string,
): UseTrxPayPageScriptResult {
  // Derive the bundle host from the app environment (prod → `.com`, else
  // `.net`). `scriptUrl` — a caller-passed env var, if any — is treated as an
  // explicit override, so an UNSET var no longer silently means UAT.
  const url = resolveTrxPayPageFieldsUrl(scriptUrl);
  const [state, setState] = useState<Omit<UseTrxPayPageScriptResult, "retry">>({
    ready: false,
    error: null,
    PayPageJS: null,
  });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setState({ ready: false, error: null, PayPageJS: null });
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadScript(url)
      .then((PayPageJS) => {
        if (!cancelled) {
          setState({ ready: true, error: null, PayPageJS });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            ready: false,
            error: err instanceof Error ? err : new Error(String(err)),
            PayPageJS: null,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [url, attempt]);

  return { ...state, retry };
}
